<script setup lang="ts">
import { hasAnalyticsConsent } from '../../composables/useAnalyticsConsent'

const PAGE_PATH = '/product'
const PAGE_TITLE = 'Product Selection | QxAIOS'
const PAGE_DESCRIPTION =
  'Choose the right QxAIOS product path for quality review, validation workflow control, and compliance insight.'
const CANONICAL_URL = 'https://qx.qiksolve.com/product'
const SOCIAL_IMAGE = 'https://qx.qiksolve.com/images/logo.svg'

useSeoMeta({
  title: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  ogTitle: PAGE_TITLE,
  ogDescription: PAGE_DESCRIPTION,
  ogType: 'website',
  ogUrl: CANONICAL_URL,
  ogImage: SOCIAL_IMAGE,
  twitterCard: 'summary_large_image',
  twitterTitle: PAGE_TITLE,
  twitterDescription: PAGE_DESCRIPTION,
  twitterImage: SOCIAL_IMAGE,
  canonical: CANONICAL_URL,
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: CANONICAL_URL,
    },
  ],
})

const { emitPageView, emitCTAClicked } = useAnalytics()

interface ProductOption {
  id: 'review' | 'verify' | 'insight'
  title: string
  route: string
  badgeLabel: string
  badgeClass: 'badge-spruce' | 'badge-gold'
  summary: string
  bestFor: string
  useWhen: string
  nextStep: string
}

const productOptions: ProductOption[] = [
  {
    id: 'review',
    title: 'QxReview',
    route: '/QxReview',
    badgeLabel: 'Entry Product',
    badgeClass: 'badge-spruce',
    summary: 'AI-driven GMP batch record review with traceable findings and governed run summaries.',
    bestFor: 'QA teams starting with paper or scanned record review bottlenecks.',
    useWhen: 'You need first-run value without platform-wide rollout pressure.',
    nextStep: 'Start with controlled review outputs, then extend governance scope.',
  },
  {
    id: 'verify',
    title: 'QxVerify',
    route: '/verify',
    badgeLabel: 'Validation Control',
    badgeClass: 'badge-spruce',
    summary: 'Creates a verified controlled electronic copy from scanned paper records with exact-copy assurance.',
    bestFor: 'Validation, QA, and records teams who must prove the electronic record exactly matches master paper.',
    useWhen: 'You need confidence to archive master paper and rely on the digitized copy for regulated decisions.',
    nextStep: 'Archive the master paper record and make trusted decisions from the verified electronic copy.',
  },
  {
    id: 'insight',
    title: 'QxInsight',
    route: '/insight',
    badgeLabel: 'Operational Signals',
    badgeClass: 'badge-gold',
    summary: 'Creates trended data reports and deep insights from records captured in QxReview and verified in QxVerify.',
    bestFor: 'Operations and quality leaders needing trusted trend visibility and deeper signal interpretation.',
    useWhen: 'You need to make decisions from governed data that has been reviewed and exact-copy verified.',
    nextStep: 'Turn captured-and-verified records into insight-led actions and continuous improvement decisions.',
  },
]

const adoptionPath = [
  {
    step: 'Start with QxReview',
    detail: 'Establish first-pass review throughput and traceable finding confidence.',
  },
  {
    step: 'Add QxVerify',
    detail: 'Establish exact-copy assurance so master paper can be archived and digital decisions remain trusted.',
  },
  {
    step: 'Expand to QxInsight',
    detail: 'Surface cross-run operational signals for continuous quality improvement.',
  },
]

onMounted(() => {
  if (hasAnalyticsConsent()) {
    emitPageView(PAGE_PATH, PAGE_TITLE)
  }
})

function trackSelection(
  location: 'hero' | 'mid' | 'footer',
  product: string,
  buttonText: string,
  destination: string
): void {
  if (hasAnalyticsConsent()) {
    emitCTAClicked(location, product, buttonText, destination)
  }
}
</script>

<template>
  <UContainer class="py-12 md:py-16 space-y-12">
    <section
      aria-labelledby="product-decision-title"
      class="space-y-6"
    >
      <div class="space-y-4 max-w-4xl">
        <p class="text-sm font-semibold uppercase tracking-[0.08em] text-spruce-700">
          QxAIOS Product Selection
        </p>
        <h1
          id="product-decision-title"
          class="text-4xl md:text-5xl font-bold text-neutral-900 tracking-tight"
        >
          Choose the Right Product Path for Your Current Quality Objective
        </h1>
        <p class="text-lg text-neutral-700 max-w-3xl">
          QxReview, QxVerify, and QxInsight support distinct compliance stages. Use this page to select the right entry
          point, then expand only when your team is ready.
        </p>
      </div>

      <div class="flex flex-wrap gap-3">
        <UButton
          to="/QxReview"
          color="primary"
          @click="trackSelection('hero', 'review', 'Start with QxReview', 'product_route')"
        >
          Start with QxReview
        </UButton>
        <UButton
          to="/verify"
          variant="outline"
          color="primary"
          @click="trackSelection('hero', 'verify', 'Evaluate QxVerify', 'product_route')"
        >
          Evaluate QxVerify
        </UButton>
        <UButton
          to="/insight"
          variant="outline"
          color="primary"
          @click="trackSelection('hero', 'insight', 'Explore QxInsight', 'product_route')"
        >
          Explore QxInsight
        </UButton>
      </div>
    </section>

    <section
      aria-labelledby="how-to-choose-title"
      class="bg-platform-subtle border border-neutral-200 rounded-xl p-6 md:p-8"
    >
      <div class="space-y-4">
        <h2
          id="how-to-choose-title"
          class="text-2xl md:text-3xl font-semibold text-neutral-900"
        >
          How to Choose
        </h2>
        <p class="text-neutral-700 max-w-4xl">
          Select based on immediate operational need: review throughput, verification control, or trend visibility. If
          you are unsure, begin with QxReview for low-friction entry, then layer QxVerify and QxInsight as governance
          depth grows.
        </p>
      </div>
    </section>

    <section
      aria-labelledby="product-comparison-title"
      class="space-y-6"
    >
      <h2
        id="product-comparison-title"
        class="text-2xl md:text-3xl font-semibold text-neutral-900"
      >
        Product Comparison
      </h2>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <UCard
          v-for="product in productOptions"
          :key="product.route"
          :class="[
            'product-option-card',
            'theme-platform',
            'h-full !bg-white dark:!bg-white !text-neutral-900 dark:!text-neutral-900 border border-neutral-200'
          ]"
        >
          <template #header>
            <div class="space-y-3">
              <UBadge
                variant="subtle"
                color="neutral"
                :class="['badge-chip', product.badgeClass]"
              >
                {{ product.badgeLabel }}
              </UBadge>
              <h3 class="text-xl font-semibold text-neutral-900">
                {{ product.title }}
              </h3>
              <p class="text-neutral-700 leading-relaxed">
                {{ product.summary }}
              </p>
            </div>
          </template>

          <div class="space-y-4 text-sm text-neutral-700">
            <div>
              <p class="font-semibold text-neutral-900 mb-1">
                Best for
              </p>
              <p>{{ product.bestFor }}</p>
            </div>
            <div>
              <p class="font-semibold text-neutral-900 mb-1">
                Use when
              </p>
              <p>{{ product.useWhen }}</p>
            </div>
            <div>
              <p class="font-semibold text-neutral-900 mb-1">
                Immediate next step
              </p>
              <p>{{ product.nextStep }}</p>
            </div>
          </div>

          <template #footer>
            <NuxtLink
              :to="product.route"
              class="inline-flex items-center text-sm font-semibold text-spruce-800 hover:text-spruce-700"
              @click="trackSelection('mid', product.id, `Open ${product.title}`, 'product_route')"
            >
              Open {{ product.title }}
            </NuxtLink>
          </template>
        </UCard>
      </div>
    </section>

    <section
      aria-labelledby="adoption-path-title"
      class="space-y-5"
    >
      <h2
        id="adoption-path-title"
        class="text-2xl md:text-3xl font-semibold text-neutral-900"
      >
        Start Small, Expand with Intent
      </h2>
      <ol class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <li
          v-for="item in adoptionPath"
          :key="item.step"
          class="border border-neutral-200 rounded-lg p-4 bg-white"
        >
          <p class="font-semibold text-neutral-900 mb-2">
            {{ item.step }}
          </p>
          <p class="text-sm text-neutral-700">
            {{ item.detail }}
          </p>
        </li>
      </ol>
    </section>

    <section
      aria-labelledby="product-cta-title"
      class="bg-white border border-neutral-200 rounded-xl p-6 md:p-8 space-y-5"
    >
      <h2
        id="product-cta-title"
        class="text-2xl md:text-3xl font-semibold text-neutral-900"
      >
        Move Forward with the Right Route
      </h2>
      <p class="text-neutral-700 max-w-3xl">
        Enter a product route directly if your objective is clear, or book a discussion when you need deployment or
        compliance guidance.
      </p>
      <div class="flex flex-wrap gap-3">
        <UButton
          to="/QxReview"
          color="primary"
          @click="trackSelection('footer', 'review', 'Start with QxReview', 'product_route')"
        >
          Start with QxReview
        </UButton>
        <UButton
          to="/contact/"
          variant="outline"
          color="primary"
          @click="trackSelection('footer', 'platform', 'Book a Discussion', 'contact')"
        >
          Book a Discussion
        </UButton>
      </div>
    </section>
  </UContainer>
</template>

<style scoped>
.product-option-card {
  border-top: 3px solid var(--product-color);
  transition: transform 180ms ease-out, box-shadow 180ms ease-out;
}

.product-option-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.04);
}

.badge-chip {
  width: fit-content;
}

.badge-spruce {
  background-color: var(--spruce-100);
  color: var(--spruce-800);
  border: 1px solid var(--spruce-200);
}

.badge-gold {
  background-color: var(--gold-50);
  color: var(--gold-700);
  border: 1px solid var(--gold-200);
}

@media (prefers-reduced-motion: reduce) {
  .product-option-card {
    transition: none;
  }

  .product-option-card:hover {
    transform: none;
  }
}
</style>
